import {
  fetchReservationInfoAction,
  fetchReserveBrandInfoAction,
  fetchAvailableDateAction,
  fetchTimeslotAction,
  setReservationInfoCache,
  setReserveBrandInfoCache,
  setAvailableDateCache
} from './action'
import { createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

interface Reservation {
  reservationsInfo: PlainInfo<Reservation.ReservationsInfo>
  brandInfo: PlainInfo<Reservation.BrandInfo>
  availableDate: PlainInfo<Reservation.OutletDate[]>
  blockedDate: PlainInfo<Reservation.OutletDate[]>
  timeslot: PlainInfo<string[]>
}

type Nullable<T> = T | null

type PlainInfo<T> = {
  status: RequestState
  error?: Record<string, string>
  data: Nullable<T>
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

const initialState: Reservation = {
  reservationsInfo: {
    status: 'none',
    data: null,
    error: null
  },
  brandInfo: {
    status: 'none',
    data: null,
    error: null
  },
  availableDate: {
    status: 'none',
    data: [],
    error: null
  },
  blockedDate: {
    status: 'none',
    data: [],
    error: null
  },
  timeslot: {
    status: 'none',
    data: [],
    error: null
  }
}

export type ReservationInReducer = {
  '@reservation': Reservation
}

export const reservationSlice = createSlice({
  name: '@reservation',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchReservationInfoAction.pending, (state) => {
        state.reservationsInfo.status = 'pending'
      })
      .addCase(fetchReservationInfoAction.fulfilled, (state, action) => {
        state.reservationsInfo.status = 'fulfilled'
        const { status, url, oddleEntityId, stores } =
          action.payload.data?.reserve || {}
        if (status === 'enabled') {
          state.reservationsInfo.data = {
            status,
            url,
            oddleEntityId,
            stores: stores.filter((store) => store?.channelData?.status === 'enabled')
          }
        } else {
          state.reservationsInfo.data = action.payload.data?.reserve || {}
        }
      })
      .addCase(fetchReservationInfoAction.rejected, (state) => {
        state.reservationsInfo.status = 'rejected'
        state.reservationsInfo.data = null
      })
      .addCase(fetchReserveBrandInfoAction.pending, (state) => {
        state.brandInfo.status = 'pending'
        state.brandInfo.data = null
      })
      .addCase(fetchReserveBrandInfoAction.fulfilled, (state, action) => {
        state.brandInfo.status = 'fulfilled'
        state.brandInfo.data = action.payload
      })
      .addCase(fetchReserveBrandInfoAction.rejected, (state, action) => {
        state.brandInfo.status = 'rejected'
        state.brandInfo.error = action.payload as Record<string, string>
      })
      .addCase(fetchAvailableDateAction.pending, (state) => {
        state.availableDate.status = 'pending'
        state.availableDate.data = null
      })
      .addCase(fetchAvailableDateAction.fulfilled, (state, action) => {
        state.availableDate.status = 'fulfilled'
        state.availableDate.data = action.payload?.filter(
          (availableDate) => !availableDate.blockedReason
        )
        state.blockedDate.data = action.payload?.filter(
          (availableDate) => availableDate.blockedReason
        )
      })
      .addCase(fetchAvailableDateAction.rejected, (state) => {
        state.availableDate.status = 'rejected'
        state.availableDate.data = null
      })
      .addCase(fetchTimeslotAction.pending, (state) => {
        state.timeslot.status = 'pending'
        state.timeslot.data = null
      })
      .addCase(fetchTimeslotAction.fulfilled, (state, action) => {
        state.timeslot.status = 'fulfilled'
        state.timeslot.data = action.payload
      })
      .addCase(fetchTimeslotAction.rejected, (state) => {
        state.timeslot.status = 'rejected'
        state.timeslot.data = null
      })
      .addCase(setReservationInfoCache, (state, action) => {
        state.reservationsInfo.status = action.payload.status
        state.reservationsInfo.data = action.payload.data
      })
      .addCase(setReserveBrandInfoCache, (state, action) => {
        state.brandInfo.status = action.payload.status
        state.brandInfo.data = action.payload.data
      })
      .addCase(setAvailableDateCache, (state, action) => {
        state.availableDate.status = action.payload.availableDate.status
        state.availableDate.data = action.payload.availableDate.data
        state.blockedDate.data = action.payload.blockedDate.data
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@reservation']
      }))
})
